/**
 * Shared cascading layer recomposition closure.
 *
 * Distinguishes two dependent sets after a visual edit:
 *
 *   recomposeTargets — layers whose *stored model* must change (automatic
 *     composition rebuild and/or metrics-key inheritance). These may be
 *     written into the authoritative Yjs packet as layer snapshots.
 *
 *   invalidateTargets — layers that only need worker/canvas/overview refresh
 *     because they *draw* an edited source (e.g. manual composites). These
 *     must NOT receive model writes; they are stamped only as
 *     `workerReplayTargets` metadata on the Yjs packet.
 *
 *   allTargets — source ∪ recompose ∪ invalidate. Stamp this as
 *     `workerReplayTargets` so local and remote peers refresh the same
 *     compile/cache closure without re-deriving it.
 *
 * Two scopes are supported:
 *
 *   `visible` — Used during live drag for transient visible-only refreshes.
 *               Transitive retain expansion includes hidden intermediates that
 *               reach a visible glyph (same rule as collectComponentDependentGlyphs
 *               with retainGlyphNames).
 *   `all`     — Used for every committed edit (keyboard, drag-end, undo,
 *               redo, remote).  This is the authoritative full closure.
 *
 * Mutation vs persistence:
 *   - Mutation gate: rebuild/metrics only change stored values when they differ.
 *   - Persistence gate: after live drag, automatic/metrics dependents still
 *     enter recomposeTargets even when mutation is a no-op, so Yjs matches
 *     the already-correct model.
 *
 * Layer identity: every target carries a per-glyph layer id resolved via
 * designspace matching. Source layer UUIDs must never be reused as dependent ids.
 *
 * The module is the sole cascade mutator for live and commit — all
 * dependencies are passed explicitly.
 */
import { recordCompileBenchRecomposition } from './compile-bench-probe';
import { deriveGlyphNameFromPath, getPathSegments, isDerivedLayerChangePath, normalizeWorkerReplayTargets } from './change-log';
function emptyClosure(sourceTargets) {
    const allTargets = normalizeWorkerReplayTargets(sourceTargets);
    const affectedGlyphNames = new Set(sourceTargets.map((t) => t.glyphName).filter(Boolean));
    return {
        allTargets,
        recomposeTargets: [],
        invalidateTargets: [],
        dependentTargets: [],
        affectedGlyphNames,
        recomposeGlyphNames: new Set(),
        invalidateGlyphNames: new Set()
    };
}
function layerSnapshotTouchesField(snapshot, field) {
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
        return false;
    }
    return field in snapshot;
}
/**
 * Infer edit kinds from buffered bridge operations so the finalizer does not
 * run a universal outline+anchor+sidebearing+component superset.
 */
export function deriveEditKindsFromOperations(operations) {
    const kinds = new Set();
    for (const operation of operations) {
        const path = operation.applyPath ?? operation.path ?? [];
        if (path.length === 5 &&
            path[0] === 'glyphs' &&
            path[2] === 'layers' &&
            path[4] === 'width') {
            kinds.add('sidebearing');
            continue;
        }
        if (path.length >= 5 &&
            path[0] === 'glyphs' &&
            path[2] === 'layers' &&
            path[4] === 'anchors') {
            kinds.add('anchor');
            continue;
        }
        if (path.length >= 5 &&
            path[0] === 'glyphs' &&
            path[2] === 'layers' &&
            path[4] === 'nodes') {
            kinds.add('outline');
            continue;
        }
        if (path.length >= 5 &&
            path[0] === 'glyphs' &&
            path[2] === 'layers' &&
            path[4] === 'shapes') {
            // Shape ops may be path geometry or component transforms.
            kinds.add('outline');
            kinds.add('component');
            continue;
        }
        if (operation.applyMode === 'layer-snapshot' &&
            path.length === 4 &&
            path[0] === 'glyphs' &&
            path[2] === 'layers') {
            const oldValue = operation.applyOldValue ?? operation.oldValue;
            const newValue = operation.applyNewValue ?? operation.newValue;
            if (layerSnapshotTouchesField(oldValue, 'width') ||
                layerSnapshotTouchesField(newValue, 'width')) {
                kinds.add('sidebearing');
            }
            if (layerSnapshotTouchesField(oldValue, 'anchors') ||
                layerSnapshotTouchesField(newValue, 'anchors')) {
                kinds.add('anchor');
            }
            if (layerSnapshotTouchesField(oldValue, 'shapes') ||
                layerSnapshotTouchesField(newValue, 'shapes')) {
                kinds.add('outline');
                kinds.add('component');
            }
        }
    }
    return kinds;
}
export function deriveEditKindsFromChangeLogEntries(entries) {
    const kinds = deriveEditKindsFromOperations(entries.map((entry) => {
        const path = getPathSegments(entry.path);
        const isLayerRoot = path.length === 4 &&
            path[0] === 'glyphs' &&
            path[2] === 'layers';
        return {
            path,
            applyMode: isLayerRoot ? 'layer-snapshot' : 'default',
            oldValue: entry.replayOldValue ?? entry.oldValue,
            newValue: entry.replayNewValue ?? entry.newValue
        };
    }));
    for (const entry of entries) {
        if (entry.compileEditType === 'anchor') {
            kinds.add('anchor');
        }
        if (entry.compileEditType === 'outline') {
            kinds.add('outline');
        }
        if (entry.compileEditType === 'component') {
            kinds.add('component');
        }
    }
    return kinds;
}
export function historyItemHasDerivedLayerWrites(item) {
    const originatingGlyphName = item.originatingGlyphName ?? null;
    if (!originatingGlyphName) {
        return false;
    }
    for (const entry of item.entries) {
        const glyphName = deriveGlyphNameFromPath(entry.path);
        if (glyphName && glyphName !== originatingGlyphName) {
            return true;
        }
    }
    return (item.workerReplayTargets ?? []).some((target) => target.glyphName !== originatingGlyphName);
}
function originEntriesForHistoryItem(item) {
    return item.entries.filter((entry) => !isDerivedLayerChangePath(entry.path, item.originatingGlyphName));
}
export function shouldResettleDerivedLayersOnHistoryReplay(item, hasFontModel) {
    if (!hasFontModel || !historyItemHasDerivedLayerWrites(item)) {
        return false;
    }
    return (deriveEditKindsFromChangeLogEntries(originEntriesForHistoryItem(item))
        .size > 0);
}
/**
 * Resolve the matching master layer on a dependent glyph.
 *
 * Layer IDs are unique per glyph. Prefer same-id lookup only as a fast path;
 * otherwise resolve via designspace location matching from the source layer.
 * Callers must not pass the source layer UUID as if it were the dependent's id.
 */
export function resolveDependentLayerTarget(fontModel, glyphName, activeLayerId, sourceLayer) {
    const glyph = fontModel.findGlyph?.(glyphName);
    if (!glyph) {
        return null;
    }
    const matchedLayer = (activeLayerId ? glyph.findLayerById?.(activeLayerId) : null) ??
        sourceLayer?.getMatchingLayerOnGlyph?.(glyphName) ??
        null;
    if (!matchedLayer?.id) {
        return null;
    }
    return { glyphName, layerId: matchedLayer.id };
}
/** @deprecated Use {@link resolveDependentLayerTarget}. */
function resolveMatchingLayerTarget(fontModel, glyphName, activeLayerId, sourceLayer) {
    return resolveDependentLayerTarget(fontModel, glyphName, activeLayerId, sourceLayer);
}
function buildTargetsForGlyphNames(fontModel, glyphNames, sourceGlyphNames, activeLayerId, sourceLayer) {
    const targets = [];
    for (const glyphName of glyphNames) {
        if (sourceGlyphNames.has(glyphName)) {
            continue;
        }
        const target = resolveMatchingLayerTarget(fontModel, glyphName, activeLayerId, sourceLayer);
        if (target) {
            targets.push(target);
        }
    }
    return normalizeWorkerReplayTargets(targets);
}
/**
 * Among component-reference candidates, keep only glyphs whose matching
 * layer is fully automatic-aligned (eligible for model recomposition /
 * Yjs persistence).
 */
function collectAutomaticAlignedDependentGlyphNames(fontModel, candidateGlyphNames, activeLayerId, sourceLayer) {
    const automaticGlyphNames = new Set();
    for (const glyphName of candidateGlyphNames) {
        const target = resolveMatchingLayerTarget(fontModel, glyphName, activeLayerId, sourceLayer);
        if (!target) {
            continue;
        }
        const layer = fontModel
            .findGlyph?.(glyphName)
            ?.findLayerById?.(target.layerId);
        if (layer?.isAutomaticAlignedLayer?.()) {
            automaticGlyphNames.add(glyphName);
        }
    }
    return automaticGlyphNames;
}
// ── Public API ─────────────────────────────────────────────────────────────
/**
 * Compute the full recomposition closure for a visual edit.
 *
 * @param options.sourceTargets   The directly edited glyph/layer pairs.
 * @param options.editKinds       Active edit kinds (outline, anchor, etc.).
 *                                Mixed selections produce a union.
 * @param options.scope           `'visible'` for live drag;
 *                                `'all'` for committed edits.
 * @param options.fontModel       Font model with the required methods.
 * @param options.activeLayerId   The active editing layer (master).
 * @param options.sourceGlyphName Optional source glyph for matching-layer
 *                                lookups on dependents.
 * @param options.suppressor      Optional bridge-like object whose
 *                                `runWithoutRecording` wraps model mutations.
 * @param options.visibleGlyphNames Glyph names considered visible.  Used only
 *                                when scope is `'visible'` to limit dependents.
 */
export function computeLayerRecompositionClosure(options) {
    const startedAt = performance.now();
    const finish = (closure) => {
        recordCompileBenchRecomposition('closure', performance.now() - startedAt, { glyphCount: closure.affectedGlyphNames.size });
        return closure;
    };
    const { sourceTargets, editKinds, scope, fontModel, activeLayerId, sourceGlyphName, suppressor, visibleGlyphNames } = options;
    if (!fontModel) {
        return finish(emptyClosure(sourceTargets));
    }
    const sourceGlyphNames = new Set(sourceTargets.map((t) => t.glyphName).filter((n) => !!n));
    const recomposeGlyphNames = new Set();
    const invalidateGlyphNames = new Set();
    const visibleSet = scope === 'visible' && visibleGlyphNames
        ? new Set(visibleGlyphNames)
        : null;
    const wrap = (fn) => {
        if (suppressor?.runWithoutRecording) {
            return suppressor.runWithoutRecording(fn);
        }
        return fn();
    };
    const touchesGeometry = editKinds.has('outline') ||
        editKinds.has('anchor') ||
        editKinds.has('sidebearing') ||
        editKinds.has('component');
    const needsMetricsPropagation = editKinds.has('sidebearing') ||
        editKinds.has('outline') ||
        editKinds.has('component');
    // ── Invalidate-only: all component-reference dependents ─────────────
    // Manual and automatic composites that *draw* the edited source need
    // cache/redraw refresh, but only automatic/metrics edges may mutate
    // stored layer data (handled below). Visible scope expands intermediates
    // that reach a retained visible glyph.
    if (touchesGeometry &&
        typeof fontModel.collectComponentDependentGlyphs === 'function') {
        const collected = wrap(() => fontModel.collectComponentDependentGlyphs(sourceGlyphNames, {
            ...(scope === 'visible' && visibleSet
                ? { retainGlyphNames: visibleSet }
                : {})
        }));
        for (const depGlyphName of collected) {
            if (!sourceGlyphNames.has(depGlyphName)) {
                invalidateGlyphNames.add(depGlyphName);
            }
        }
    }
    // Allowed set for live mutation:
    // - component retain expansion (hidden intermediates → visible)
    // - all currently visible glyphs (so metrics-key dependents like `n`
    //   that reference the source without being component users still update)
    const visibleMetricsPrerequisites = scope === 'visible' &&
        visibleSet &&
        typeof fontModel.collectMetricsKeyPrerequisiteGlyphs === 'function'
        ? wrap(() => fontModel.collectMetricsKeyPrerequisiteGlyphs(visibleSet))
        : new Set();
    const allowedGlyphNames = scope === 'visible' && visibleSet
        ? new Set([
            ...sourceGlyphNames,
            ...invalidateGlyphNames,
            ...visibleSet,
            // Visible keyed glyphs require their hidden metric sources
            // to settle too; otherwise live and all-scope commit passes
            // resolve the same key chain against different values.
            ...visibleMetricsPrerequisites
        ])
        : null;
    // Invalidate only source ∪ component dependents (plus hidden metric
    // prerequisites that feed visible keyed glyphs). Do not walk every
    // currently visible glyph's layout cache on each live tick.
    const layoutInvalidationGlyphNames = new Set([
        ...sourceGlyphNames,
        ...invalidateGlyphNames,
        ...visibleMetricsPrerequisites
    ]);
    // ── Model recomposition: automatic composites ───────────────────────
    // rebuildAutomaticCompositesForGlyphs already gates on
    // isAutomaticAlignedLayer() and returns only glyphs whose stored
    // placement/width actually changed.
    if (touchesGeometry &&
        typeof fontModel.rebuildAutomaticCompositesForGlyphs === 'function') {
        const preferredTarget = sourceTargets[0] ?? null;
        const affectedComposites = wrap(() => {
            if (layoutInvalidationGlyphNames.size > 0 &&
                typeof fontModel.invalidateLayoutCachesForGlyphs === 'function') {
                fontModel.invalidateLayoutCachesForGlyphs(layoutInvalidationGlyphNames);
            }
            return fontModel.rebuildAutomaticCompositesForGlyphs(sourceGlyphNames, {
                ...(allowedGlyphNames ? { allowedGlyphNames } : {}),
                ...(preferredTarget?.layerId
                    ? {
                        preferredLayerId: preferredTarget.layerId,
                        preferredSourceGlyphName: preferredTarget.glyphName
                    }
                    : {})
            });
        });
        for (const glyphName of affectedComposites) {
            recomposeGlyphNames.add(glyphName);
        }
    }
    // ── Model recomposition: metrics-key + automatic-offset dependents ──
    // Outline edits are included because moving the visual edge of a keyed
    // source glyph must refresh inherited sidebearings. Anchor-only edits
    // do not drive metrics-key inheritance. Automatic layers are mutated only
    // via rebuild above — never via metrics translate/bake.
    if (needsMetricsPropagation &&
        typeof fontModel.recomputeMetricsKeys === 'function') {
        const metricsDeps = wrap(() => fontModel.recomputeMetricsKeys(sourceGlyphNames, {
            ...(allowedGlyphNames ? { allowedGlyphNames } : {}),
            // The closure already performed the initial source-component
            // rebuild above. Keep that work single-pass, but allow the
            // metrics queue to rebuild automatic dependents after a keyed
            // glyph changes (l -> n -> a -> adieresis).
            skipInitialAutomaticCompositeRebuild: true
        }));
        for (const depGlyphName of metricsDeps) {
            recomposeGlyphNames.add(depGlyphName);
        }
    }
    const sourceLayer = sourceGlyphName && activeLayerId
        ? (fontModel
            .findGlyph?.(sourceGlyphName)
            ?.findLayerById?.(activeLayerId) ?? null)
        : null;
    // Persist automatic composites / metrics-key dependents even when the
    // rebuild/metrics pass is a no-op. Live drag may already have applied the
    // derived state; omitting those layers from changedLayerTargets leaves
    // stale Yjs data that post-commit overlay clear reveals as a rollback
    // (e.g. Adieresis mark stuck at the pre-drag transform).
    //
    // Anchor edits must expand the same way as sidebearing/outline: after a
    // live visible-scope converge, commit-time rebuild returns [] even when
    // composition-relevant anchors moved. Gating on rebuild mutations here
    // was the stale-compiled-mark bug.
    const shouldPersistAutomaticDependents = editKinds.has('sidebearing') ||
        editKinds.has('outline') ||
        editKinds.has('component') ||
        editKinds.has('anchor') ||
        [...recomposeGlyphNames].some((glyphName) => !sourceGlyphNames.has(glyphName));
    if (shouldPersistAutomaticDependents) {
        for (const glyphName of collectAutomaticAlignedDependentGlyphNames(fontModel, invalidateGlyphNames, activeLayerId, sourceLayer)) {
            recomposeGlyphNames.add(glyphName);
        }
    }
    if ((editKinds.has('sidebearing') ||
        editKinds.has('outline') ||
        editKinds.has('component')) &&
        typeof fontModel.collectMetricsKeyDependentGlyphs === 'function') {
        for (const glyphName of fontModel.collectMetricsKeyDependentGlyphs(sourceGlyphNames)) {
            if (allowedGlyphNames && !allowedGlyphNames.has(glyphName)) {
                continue;
            }
            recomposeGlyphNames.add(glyphName);
        }
    }
    const recomposeTargets = buildTargetsForGlyphNames(fontModel, recomposeGlyphNames, sourceGlyphNames, activeLayerId, sourceLayer);
    const invalidateOnlyGlyphNames = new Set([...invalidateGlyphNames].filter((glyphName) => !recomposeGlyphNames.has(glyphName)));
    const invalidateTargets = buildTargetsForGlyphNames(fontModel, invalidateOnlyGlyphNames, sourceGlyphNames, activeLayerId, sourceLayer);
    const dependentTargets = normalizeWorkerReplayTargets([
        ...recomposeTargets,
        ...invalidateTargets
    ]);
    const allTargets = normalizeWorkerReplayTargets([
        ...sourceTargets,
        ...dependentTargets
    ]);
    const affectedGlyphNames = new Set([
        ...sourceGlyphNames,
        ...recomposeGlyphNames,
        ...invalidateGlyphNames
    ]);
    return finish({
        allTargets,
        recomposeTargets,
        invalidateTargets,
        dependentTargets,
        affectedGlyphNames,
        recomposeGlyphNames,
        invalidateGlyphNames
    });
}
/**
 * Convenience shortcut: derive the set of edit kinds from a drag type and
 * optional selection-state flags.  This ensures every drag-end path produces
 * the same union closure as a keyboard edit with the same selection.
 */
export function deriveEditKindsFromDrag(dragType, hasGeometryInSelection, hasAnchorsInSelection, hasComponentsInSelection) {
    const kinds = new Set();
    switch (dragType) {
        case 'point':
            kinds.add('outline');
            break;
        case 'anchor':
            kinds.add('anchor');
            break;
        case 'component':
            kinds.add('component');
            break;
        case 'sidebearing':
            kinds.add('sidebearing');
            break;
        case 'transform':
            if (hasGeometryInSelection)
                kinds.add('outline');
            if (hasAnchorsInSelection)
                kinds.add('anchor');
            if (hasComponentsInSelection)
                kinds.add('component');
            break;
        default:
            // Fallback: add only outline as a safe default.
            kinds.add('outline');
            break;
    }
    if (kinds.size === 0) {
        kinds.add('outline');
    }
    return kinds;
}
/**
 * Resolve Yjs layer-sync targets from a closure result.
 * Replay stays wide; changed-layer snapshots stay lean (source + recompose).
 */
export function resolveLayerSyncTargetsFromClosure(closure, sourceTargets, previouslyRecomposedTargets = []) {
    const normalizedSource = normalizeWorkerReplayTargets(sourceTargets);
    return {
        changedLayerTargets: normalizeWorkerReplayTargets([
            ...normalizedSource,
            // Live drag may already have converged visible descendants before
            // the all-scope commit closure runs. A no-op final pass must still
            // snapshot those layers into Yjs; otherwise the worker retains
            // their pre-drag state and the full commit reshape rolls them back.
            ...previouslyRecomposedTargets,
            ...closure.recomposeTargets
        ]),
        workerReplayTargets: normalizeWorkerReplayTargets([
            ...previouslyRecomposedTargets,
            ...(closure.allTargets.length > 0
                ? closure.allTargets
                : normalizedSource)
        ])
    };
}
